import RENOVATION_STATUS from './RENOVATION_STATUS'

import Improvement from './improvement'

import MathTools from '@/helpers/math-tools'

const validStatuses = Object.values(RENOVATION_STATUS)

/**
 * Defined structure of a project.
 * A Project represents the state model for a Renovation plan that is being either built or updated.
 * It is distinct from a Renovation or RenovationDataSubmission in that the Project model
 * contains transient information to drive the builder, such as having all improvements available for
 * the scheme, tagged as recommended or selected, whereas a Renovation only contains the selected improvements.
 * @export Project
 * @class Project
 */
export default class Project {

    /**
     * Creates an instance of a project.
     * @param {?Object} data Optional Source data - From API or state.
     * @param {boolean} fromRenovation A flag to indicate that the source data is a renovation.
     * @memberof Project
     */
    constructor(data = null, fromRenovation = false) {

        /**
         * @type {?string}
         */
        this.name = data && data.name || null

        /**
         * @type {boolean}
         */
        this.initialised = fromRenovation || data && data.initialised || false

        /**
         * @type {?number}
         */
        this.id = data && data.id ? parseInt(data.id) : null

        /**
         * @type {string}
         */
        this.status = data && data.status || RENOVATION_STATUS.planning

        /**
         * @type {?string}
         */
        this.motivation = data && data.motivation || null

        /**
         * @type {?number}
         */
        this.budget = data && data._budget || data && data.budget || null

        /**
         * @type {string|Date|null}
         */
        this.asOf = data && (data.dateStamp || data.asOf) || null

        /**
         * @type {Array<Improvement>}
         */
        this.improvements = data && data.improvements
            ? data.improvements.map(item => Improvement.ensure(item))
            : []

        /**
         * @type {Array<{key, value}>}
         */
        if (data && data.notes) this.notes = data.notes

        /**
         * @type {Array<{key, value}>}
         */
        this.services = data && data.services || []

        if (fromRenovation)
            this.improvements.forEach(imp => imp.accepted = true)
    }

    /**
     * Gets the filtered improvements that have been selected for this project.
     * @readonly
     * @returns {Array<Improvement>}
     * @memberof Project
     */
    get addedImprovements() {
        return this.improvements ? this.improvements.filter(imp => imp.accepted) : []
    }

    /**
     * Gets the filtered improvements that have both either recommended or have been selected for this project.
     * @readonly
     * @returns {Array<Improvement>}
     * @memberof Project
     */
    get addedOrRecommendedImprovements() {
        return this.improvements ? this.improvements.filter(imp => imp.recommended || imp.accepted) : []
    }

    /**
     * Gets the filtered improvements that are available for selection.
     * @readonly
     * @returns {Array<Improvement>}
     * @memberof Project
     */
    get availableImprovements() {
        return this.improvements ? this.improvements : []
    }

    /**
     * Gets the formatted display of the budget value.
     * @readonly
     * @returns {string}
     * @memberof Project
     */
    get budgetDisplay() {
        return this.budget < Number.MAX_VALUE ? MathTools.segmentCurrency(this.budget) : 'an unlimited budget'
    }

    /**
     * Gets the cost of the project.
     * @readonly
     * @returns {number}
     * @memberof Project
     */
    get cost() {
        const costs = this.addedImprovements.map(item => item.price)
        return costs && costs.length ? costs.reduce((sum, item) => sum + item) : 0
    }

    /**
     * Gets the formatted display of the cost of the project.
     * @readonly
     * @returns {string}
     * @memberof Project
     */
    get costDisplay() {
        return MathTools.segmentCurrency(this.cost)
    }

    /**
     * Gets the filtered improvements that are recommended by applied criteria.
     * @readonly
     * @returns {Array<Improvement>}
     * @memberof Project
     */
    get recommendedImprovements() {
        return this.improvements ? this.improvements.filter(item => item.recommended) : []
    }

    /**
     * Gets the status value.
     * @returns {string}
     * @memberof Project
     */
    get status() {
        return this._status
    }

    /**
     * Sets the status value, limited by valid options.
     * @param {string} value
     * @memberof Project
     */
    set status(value) {
        if (this._status === RENOVATION_STATUS.updating && value !== RENOVATION_STATUS.submitted) return
        if (value && !validStatuses.includes(value))
            throw new Error('Invalid status provided for the project.')

        this._status = value
    }

    /**
     * Returns true if the scheme has the specified service.
     * @param {string} serviceKey
     * @returns {boolean}
     */
    hasService(serviceKey) {
        return this.services.filter(service => service.key === serviceKey && service.value === 'Y').length >= 1
    }

    /**
     * Clears the notes array for the project.
     * @memberof Project
     */
    clearNotes() {
        delete this.notes
    }

    /**
     * Returns the object in a form suitable to store.
     * @returns {Object}
     * @memberof Project
     */
    toStoreJson() {
        const retObj = {
            name: this.name,
            status: this.status,
            motivation: this.motivation,
            budget: this.budget,
            improvements: this.improvements.map(item => item.toStoreJson()),
            initialised: this.initialised,
            asOf: this.asOf,
            notes: this.notes,
        }

        if (!this.notes) delete retObj.notes

        return retObj
    }

    /**
     * Ensures that the provided data is already of this type. If not, it runs the constructor.
     * @static
     * @param {?Object} data Source data - From API or state.
     * @returns {Project} Either the data is passed back if already of this type or the result of the constructor is.
     * @memberof Project
     */
    static ensure(data = null) {
        return data && data instanceof Project ? data : new Project(data)
    }

    /**
     * Returns the object type from raw stored JSON data.
     * @static
     * @param {?Object} data The stored data.
     * @returns {Project}
     * @memberof Project
     */
    static fromStoreJson(data = null) {
        return data ? new Project(data) : null
    }
}
